import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Briefcase, ChevronLeft, MailCheck } from 'lucide-react'
import { toast } from 'sonner'

import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { authApi } from '@/services/auth'
import { orgTreeApi } from '@/services/org-tree'

const maskEmailAddress = (email: string) => {
  const [localPart, domain = ''] = email.split('@')

  if (!localPart) {
    return email
  }

  const visiblePrefix = localPart.slice(0, Math.min(2, localPart.length))
  const maskedLocal = `${visiblePrefix}${'*'.repeat(Math.max(localPart.length - visiblePrefix.length, 0))}`

  return domain ? `${maskedLocal}@${domain}` : maskedLocal
}

const getOtpLength = (rawValue: string | null) => {
  const parsedValue = Number(rawValue)

  if (!Number.isFinite(parsedValue) || parsedValue < 4) {
    return 6
  }

  return Math.min(parsedValue, 8)
}

export default function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const email = searchParams.get('email')?.trim() || ''
  const tenant = searchParams.get('tenant')?.trim() || ''
  const orgReference = searchParams.get('org')?.trim() || tenant
  const [otpLength, setOtpLength] = useState(() => getOtpLength(searchParams.get('otpLength')))
  const [code, setCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const organizationQuery = useQuery({
    queryKey: ['verify-email-service-instance', selectedBackendServerUrl, orgReference],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(orgReference),
    enabled: orgReference.length > 0,
    staleTime: 60_000,
    retry: false,
  })

  const organizationName = useMemo(() => (
    organizationQuery.data?.name
      || organizationQuery.data?.title
      || organizationQuery.data?.identifier
      || organizationQuery.data?.url_identifier
      || 'Organization'
  ), [organizationQuery.data])

  const organizationLogo = organizationQuery.data?.login_page_logo
    || organizationQuery.data?.logo
    || organizationQuery.data?.welcome_page_logo
    || null

  const loginParams = new URLSearchParams()
  if (tenant) {
    loginParams.set('tenant', tenant)
  }
  const loginHref = loginParams.toString() ? `/login?${loginParams.toString()}` : '/login'

  const handleVerify = async () => {
    const normalizedCode = code.trim()

    if (!email) {
      setErrorMessage('Verification email is missing. Start signup again and request a new code.')
      return
    }

    if (!normalizedCode) {
      setErrorMessage('Verification code is required.')
      return
    }

    if (normalizedCode.length !== otpLength) {
      setErrorMessage(`Enter the ${otpLength}-digit verification code.`)
      return
    }

    setErrorMessage('')
    setIsSubmitting(true)
    try {
      await authApi.verifySignupEmailOtp({
        email,
        code: normalizedCode,
      })

      toast.success('Email verified. Sign in to continue.')
      navigate(loginHref, { replace: true })
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!email || !orgReference) {
      setErrorMessage('Verification session is incomplete. Start signup again to request a new code.')
      return
    }

    setErrorMessage('')
    setIsResending(true)
    try {
      const response = await authApi.resendSignupVerificationEmail({
        email,
        org_instance_id: orgReference,
      })

      const nextOtpLength = typeof response.data?.otp_length === 'number'
        ? response.data.otp_length
        : otpLength
      setOtpLength(nextOtpLength > 0 ? nextOtpLength : otpLength)
      toast.success(response.message || 'A new verification code has been sent.')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setIsResending(false)
    }
  }

  if (!email) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardHeader>
            <CardTitle>Verification session unavailable</CardTitle>
            <CardDescription>We need your email address to verify the signup code.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <BackendServerSelector className="w-full" />
            <Button asChild className="w-full">
              <Link to="/signup">Start signup again</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to={loginHref}>Go to sign in</Link>
            </Button>
          </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          {organizationLogo ? (
            <img src={organizationLogo} alt={`${organizationName} logo`} className="max-h-24 w-auto rounded-xl border bg-card p-2" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Briefcase className="h-7 w-7" />
            </div>
          )}
          <h1 className="mt-4 text-2xl font-bold text-foreground">Verify your email</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the {otpLength}-digit code sent to {maskEmailAddress(email)}.
          </p>
        </div>

        <Card className="border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardHeader className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl">Email verification</CardTitle>
                <CardDescription>{organizationName}</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to={loginHref}>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <BackendServerSelector disabled={isSubmitting || isResending} className="w-full" />

            {organizationQuery.isLoading ? (
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                Loading organization details...
              </div>
            ) : null}

            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <div className="flex items-start gap-2">
                <MailCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Use the code from your verification email to finish activating this account.</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="verification_code">Verification code</Label>
              <Input
                id="verification_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={otpLength}
                placeholder={`${otpLength}-digit code`}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, '').slice(0, otpLength))
                  if (errorMessage) {
                    setErrorMessage('')
                  }
                }}
                disabled={isSubmitting}
                aria-invalid={Boolean(errorMessage)}
              />
              {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
            </div>

            <div className="flex gap-3">
              <Button type="button" className="flex-1" disabled={isSubmitting} onClick={handleVerify}>
                {isSubmitting ? <Spinner className="mr-2" /> : null}
                Verify email
              </Button>
              <Button type="button" variant="outline" className="flex-1" disabled={isSubmitting || isResending} onClick={handleResend}>
                {isResending ? <Spinner className="mr-2" /> : null}
                Resend code
              </Button>
            </div>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Already verified? </span>
              <Link to={loginHref} className="font-medium text-primary hover:underline">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}